<template>
  <div class="bp-item-form" v-loading="loading">
    <!-- Page head -->
    <div class="bp-form-head">
      <div class="bp-form-head__left">
        <button type="button" class="bp-back-btn" @click="goBack">
          <span class="bp-back-btn__icon">‹</span>
        </button>
        <div>
          <h2 class="bp-form-title">{{ isEdit ? "Bandni tahrirlash" : "Yangi band" }}</h2>
          <p class="bp-form-sub">Byudjet jarayoni Gantt chartidagi bitta band (bar)</p>
        </div>
      </div>
      <el-tag v-if="isEdit" type="info" effect="plain" round>ID: {{ itemId }}</el-tag>
    </div>

    <el-alert
        v-if="!groupsLoading && !groups.length"
        type="warning"
        show-icon
        :closable="false"
        class="bp-no-groups"
        title="Guruhlar topilmadi"
        description="Band yaratishdan oldin 'Guruhlar' bo'limida kamida bitta guruh yarating."
    />

    <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="bp-form">
      <div class="bp-form-top">
        <section class="bp-card">
            <header class="bp-card__head">
              <span class="bp-card__dot"/>
              <h3>Asosiy ma'lumot</h3>
            </header>
            <div class="bp-card__body">
              <el-form-item label="Guruh" prop="group_id">
                <el-select
                    v-model="form.group_id"
                    placeholder="Guruhni tanlang"
                    filterable
                    style="width: 100%"
                    :loading="groupsLoading"
                >
                  <el-option
                      v-for="g in groups"
                      :key="g.id"
                      :label="displayLangText(g.title) || '...'"
                      :value="g.id"
                  >
                    <span class="bp-opt">
                      <span class="bp-opt__swatch" :style="{background: g.color || '#64748B'}"/>
                      {{ displayLangText(g.title) || "..." }}
                    </span>
                  </el-option>
                </el-select>
              </el-form-item>

              <el-row :gutter="12">
                <el-col :span="12">
                  <el-form-item label="Tartib raqami">
                    <el-input-number v-model="form.order_number" :min="0" controls-position="right" style="width: 100%"/>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="Holati">
                    <el-switch
                        v-model="form.is_active"
                        inline-prompt
                        active-text="Faol"
                        inactive-text="Nofaol"
                        style="--el-switch-on-color: #16A34A"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
          </section>

        <section class="bp-card">
            <header class="bp-card__head">
              <span class="bp-card__dot"/>
              <h3>Davr</h3>
              <span class="bp-card__hint">oy aniqligida (kun ahamiyatsiz)</span>
            </header>
            <div class="bp-card__body">
              <div class="bp-period">
                <el-form-item label="Boshlanishi" prop="start_month" class="bp-period__field">
                  <el-date-picker
                      v-model="startPeriod"
                      type="month"
                      value-format="YYYY-MM"
                      format="MM.YYYY"
                      placeholder="Oy / Yil"
                      style="width: 100%"
                  />
                </el-form-item>
                <span class="bp-period__arrow">→</span>
                <el-form-item label="Tugashi" prop="end_month" class="bp-period__field">
                  <el-date-picker
                      v-model="endPeriod"
                      type="month"
                      value-format="YYYY-MM"
                      format="MM.YYYY"
                      placeholder="Oy / Yil"
                      :disabled-date="disabledEndDate"
                      style="width: 100%"
                  />
                </el-form-item>
              </div>
              <div v-if="periodPreviewText" class="bp-period__preview">
                <span class="bp-period__preview-icon">🗓</span>
                {{ periodPreviewText }}
                <span class="bp-period__preview-span">· {{ spanMonths }} oy</span>
              </div>
            </div>
        </section>
      </div>

      <section class="bp-card bp-card--full">
            <header class="bp-card__head">
              <span class="bp-card__dot"/>
              <h3>Nomi va kontent</h3>
              <span class="bp-card__hint">barcha tillarda</span>
            </header>
            <div class="bp-card__body">
              <el-tabs v-model="activeLang" class="bp-lang-tabs">
                <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
                  <template #label>
                    <span class="bp-lang-tab" :class="{'is-error': langErrors.includes(lt.code)}">
                      {{ lt.tabLabel }}
                      <span
                          class="bp-lang-tab__dot"
                          :class="{'is-filled': !!form.title[lt.code]}"
                          :title="form.title[lt.code] ? 'To\'ldirilgan' : 'Sarlavha bo\'sh'"
                      />
                    </span>
                  </template>

                  <el-form-item :label="'Sarlavha — ' + langLabels[lt.code]" :prop="'title.' + lt.code">
                    <el-input
                        v-model="form.title[lt.code]"
                        :placeholder="'Bar ichida ko\'rinadigan qisqa nom (' + langLabels[lt.code] + ')'"
                        clearable
                        maxlength="200"
                        show-word-limit
                        @input="clearLangError(lt.code)"
                    />
                  </el-form-item>
                  <el-form-item :label="'Kontent (Markdown) — ' + langLabels[lt.code]">
                    <MfMarkdownEditor v-model="form.content[lt.code]"/>
                  </el-form-item>
                </el-tab-pane>
              </el-tabs>
            </div>
      </section>

      <!-- Sticky action bar -->
      <div class="bp-actionbar">
        <span v-if="isDirty()" class="bp-actionbar__dirty">● Saqlanmagan o'zgarishlar</span>
        <div class="bp-actionbar__btns">
          <el-button plain @click="goBack">Bekor qilish</el-button>
          <el-button type="primary" :loading="submitting" @click="onSubmit">
            {{ isEdit ? "O'zgarishlarni saqlash" : "Yaratish" }}
          </el-button>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script>
import {ElMessage, ElMessageBox, ElNotification} from "element-plus";
import MfMarkdownEditor from "@/components/core/MfMarkdownEditor.vue";
import {
  API_BASE,
  LANG_TABS,
  LANG_LABELS,
  MONTH_NAMES_FULL,
  emptyMultiLang,
  fillMultiLang,
  displayLangText,
  periodLabel,
  absMonth,
  isPeriodOrderValid,
  toPickerValue,
  fromPickerValue,
} from "./budgetProcessTypes";

const requiredRule = {required: true, message: "Majburiy maydon", trigger: "blur"};

export default {
  name: "BudgetProcessItemForm",
  components: {MfMarkdownEditor},
  data() {
    return {
      loading: false,
      groupsLoading: false,
      submitting: false,
      groups: [],
      activeLang: "uz_lat",
      langErrors: [],
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
      initialSnapshot: null,
      allowLeave: false,
      form: {
        group_id: null,
        title: emptyMultiLang(),
        content: emptyMultiLang(),
        start_year: null,
        start_month: null,
        end_year: null,
        end_month: null,
        order_number: 0,
        is_active: true,
      },
      rules: {
        group_id: [{required: true, message: "Guruhni tanlang", trigger: "change"}],
        "title.uz_lat": [requiredRule],
        "title.uz_cyr": [requiredRule],
        start_month: [
          {required: true, message: "Boshlanish oyini tanlang", trigger: "change"},
          {validator: this.validatePeriodOrder, trigger: "change"},
        ],
        end_month: [
          {required: true, message: "Tugash oyini tanlang", trigger: "change"},
          {validator: this.validatePeriodOrder, trigger: "change"},
        ],
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    itemId() {
      return this.$route.params.id;
    },
    // Two independent month pickers (CONSOLE.md 5.2: "two month+year pickers").
    // value-format="YYYY-MM" — no days, matches the year+month backend fields.
    startPeriod: {
      get() {
        return toPickerValue(this.form.start_year, this.form.start_month);
      },
      set(val) {
        const {year, month} = fromPickerValue(val);
        this.form.start_year = year;
        this.form.start_month = month;
        this.onPeriodChange();
      },
    },
    endPeriod: {
      get() {
        return toPickerValue(this.form.end_year, this.form.end_month);
      },
      set(val) {
        const {year, month} = fromPickerValue(val);
        this.form.end_year = year;
        this.form.end_month = month;
        this.onPeriodChange();
      },
    },
    periodPreviewText() {
      if (!this.form.start_year || !this.form.end_year) return "";
      return `${periodLabel(this.form.start_year, this.form.start_month, MONTH_NAMES_FULL)} — ${periodLabel(this.form.end_year, this.form.end_month, MONTH_NAMES_FULL)}`;
    },
    spanMonths() {
      if (!this.form.start_year || !this.form.end_year) return 0;
      const diff = absMonth(this.form.end_year, this.form.end_month) - absMonth(this.form.start_year, this.form.start_month) + 1;
      return diff > 0 ? diff : 0;
    },
  },
  methods: {
    displayLangText,
    clearLangError(code) {
      this.langErrors = this.langErrors.filter((c) => c !== code);
    },
    // Grey out end months strictly before the chosen start month.
    disabledEndDate(date) {
      if (!this.form.start_year || !this.form.start_month) return false;
      const startAbs = absMonth(this.form.start_year, this.form.start_month);
      const cellAbs = absMonth(date.getFullYear(), date.getMonth() + 1);
      return cellAbs < startAbs;
    },
    validatePeriodOrder(rule, value, callback) {
      const {start_year, start_month, end_year, end_month} = this.form;
      if (!isPeriodOrderValid(start_year, start_month, end_year, end_month)) {
        callback(new Error("Tugash davri boshlanish davridan oldin bo'lishi mumkin emas"));
      } else {
        callback();
      }
    },
    onPeriodChange() {
      this.$nextTick(() => {
        this.$refs.formRef?.validateField(["start_month", "end_month"]);
      });
    },
    snapshotForm() {
      return JSON.stringify(this.form);
    },
    isDirty() {
      return this.initialSnapshot !== null && this.snapshotForm() !== this.initialSnapshot;
    },
    handleApiError(error) {
      const data = error?.response?.data;
      const message = data?.message || "Iltimos, qayta urinib ko'ring.";
      const titleMap = {NOTFOUND: "Topilmadi", BADREQUEST: "Noto'g'ri ma'lumot"};
      ElNotification({title: titleMap[data?.type] || "Xatolik", message, type: "error"});
      if (data?.invalid_args && typeof data.invalid_args === "object") {
        Object.entries(data.invalid_args).forEach(([field, detail]) => {
          ElMessage.error(`${field}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
        });
      }
    },
    fetchGroups() {
      this.groupsLoading = true;
      this.$http
          .get(`${API_BASE}/groups`)
          .then(({data}) => {
            this.groups = Array.isArray(data) ? data : [];
          })
          .catch((error) => this.handleApiError(error))
          .finally(() => {
            this.groupsLoading = false;
          });
    },
    applyItemData(data) {
      this.form.group_id = data.group_id;
      this.form.title = fillMultiLang(data.title);
      this.form.content = fillMultiLang(data.content);
      this.form.start_year = data.start_year;
      this.form.start_month = data.start_month;
      this.form.end_year = data.end_year;
      this.form.end_month = data.end_month;
      this.form.order_number = data.order_number ?? 0;
      this.form.is_active = data.is_active !== false;
    },
    loadItem() {
      this.loading = true;
      this.$http
          .get(`${API_BASE}/items/${this.itemId}`)
          .then(({data}) => {
            this.applyItemData(data);
            this.$nextTick(() => {
              this.initialSnapshot = this.snapshotForm();
            });
          })
          .catch((error) => {
            this.handleApiError(error);
            if (error?.response?.data?.type === "NOTFOUND") {
              this.allowLeave = true;
              this.$router.push("/app/budget-process?tab=items");
            }
          })
          .finally(() => {
            this.loading = false;
          });
    },
    buildPayload() {
      return {
        group_id: this.form.group_id,
        title: {...this.form.title},
        content: {...this.form.content},
        start_year: Number(this.form.start_year),
        start_month: Number(this.form.start_month),
        end_year: Number(this.form.end_year),
        end_month: Number(this.form.end_month),
        order_number: Number(this.form.order_number) || 0,
        is_active: this.form.is_active,
      };
    },
    onSubmit() {
      this.langErrors = [];
      this.$refs.formRef.validate((valid, invalidFields) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          if (invalidFields) {
            this.langErrors = Object.keys(invalidFields)
                .filter((key) => key.startsWith("title."))
                .map((key) => key.split(".")[1]);
            if (this.langErrors[0]) this.activeLang = this.langErrors[0];
          }
          return;
        }
        const payload = this.buildPayload();
        this.submitting = true;
        const request = this.isEdit
            ? this.$http.put(`${API_BASE}/items/${this.itemId}`, payload)
            : this.$http.post(`${API_BASE}/items`, payload);
        request
            .then(() => {
              this.allowLeave = true;
              ElNotification({title: "Tabriklaymiz", message: "Band muvaffaqiyatli saqlandi", type: "success"});
              this.$router.push("/app/budget-process?tab=items");
            })
            .catch((error) => this.handleApiError(error))
            .finally(() => {
              this.submitting = false;
            });
      });
    },
    goBack() {
      this.$router.push("/app/budget-process?tab=items");
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowLeave || !this.isDirty()) {
      next();
      return;
    }
    ElMessageBox.confirm(
        "Saqlanmagan o'zgarishlar bor. Sahifadan chiqishni xohlaysizmi?",
        "Diqqat!",
        {confirmButtonText: "Ha, chiqish", cancelButtonText: "Qolish", type: "warning"}
    )
        .then(() => next())
        .catch(() => next(false));
  },
  mounted() {
    this.fetchGroups();
    if (this.isEdit) {
      this.loadItem();
    } else {
      const queryGroupId = this.$route.query.group_id ? Number(this.$route.query.group_id) : null;
      if (queryGroupId) this.form.group_id = queryGroupId;
      this.$nextTick(() => {
        this.initialSnapshot = this.snapshotForm();
      });
    }
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

.bp-item-form {
  // The last card keeps clear of the sticky action bar that floats over it.
  padding-bottom: 12px;
}

/* ---- head ---- */
.bp-form-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
}

.bp-back-btn {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid $border;
  border-radius: 10px;
  background: #fff;
  color: $brand;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &__icon {
    font-size: 24px;
    line-height: 1;
    margin-top: -2px;
  }

  &:hover {
    background: $brand;
    border-color: $brand;
    color: #fff;
    transform: translateX(-2px);
  }
}

.bp-form-title {
  margin: 0;
  font-family: "Montserrat-Bold", sans-serif;
  font-size: 20px;
  color: $navy;
}

.bp-form-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: $muted;
}

.bp-no-groups {
  margin-bottom: 18px;
  border-radius: 10px;
}

/* ---- layout ---- */
.bp-form-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: stretch; // Davr & Asosiy ma'lumot cards share the same height
  margin-bottom: 18px;
}

@media (max-width: 900px) {
  .bp-form-top {
    grid-template-columns: 1fr;
  }
}

/* ---- card ---- */
.bp-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 30, 80, 0.04);
  overflow: hidden;

  &--full {
    width: 100%;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    border-bottom: 1px solid $border;
    background: linear-gradient(180deg, #fbfdff, #f6f9fc);

    h3 {
      margin: 0;
      font-family: "Montserrat-Bold", sans-serif;
      font-size: 14px;
      color: $navy;
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $brand;
    box-shadow: 0 0 0 3px rgba(0, 119, 145, 0.15);
  }

  &__hint {
    margin-left: auto;
    font-size: 11px;
    color: $muted;
  }

  &__body {
    padding: 16px 18px 6px;
  }
}

/* ---- group option ---- */
.bp-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &__swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
  }
}

/* ---- period ---- */
.bp-period {
  display: flex;
  align-items: flex-end;
  gap: 12px;

  &__field {
    flex: 1;
    margin-bottom: 0;
  }

  &__arrow {
    padding-bottom: 8px;
    color: $muted;
    font-size: 18px;
  }

  &__preview {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding: 10px 14px;
    background: rgba(0, 119, 145, 0.06);
    border: 1px solid rgba(0, 119, 145, 0.15);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: $brand;
  }

  &__preview-span {
    color: $muted;
    font-weight: 500;
  }
}

/* ---- language tabs ---- */
.bp-lang-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.is-error {
    color: #f56c6c;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #dcdfe6;
    transition: background 0.15s ease;

    &.is-filled {
      background: #16a34a;
    }
  }
}

/* ---- sticky action bar ---- */
.bp-actionbar {
  position: sticky;
  // The scroll container (.page-container) has 15px padding, so bottom:0 would
  // pin the bar 15px above the true edge and leak content through that gap.
  // -15px absorbs that padding so the bar sits flush at the very bottom.
  bottom: -15px;
  left: 0;
  right: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin: 18px -15px -15px;
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid $border;
  box-shadow: 0 -4px 14px rgba(15, 30, 80, 0.06);

  &__dirty {
    font-size: 12px;
    color: #e6a23c;
    font-weight: 600;
  }

  &__btns {
    display: flex;
    gap: 10px;
  }
}
</style>
